import { DIAGNOSTIC_DEFINITION_KIND } from "@repo/api";
import type { AddOrderInput, OrderSetChange } from "@repo/api/schemas";
import {
  asMemberId,
  asPlannedTrialId,
  materializeDefinition,
  OrderSet,
  type OrderSetCurationChange,
  type OrderSetProps,
  type OrderSetSchedulingFacts,
  type ParameterAnswer,
} from "@repo/domain";
import { getDataStoreClient } from "../core/provider";
import { readProtocolDefinition } from "../fhir/protocol-definition-storage";
import type { ActivityDefinitionRow, DeviceDefinitionRow, PlanDefinitionRow } from "../fhir/types";
import { projectDeviceDefinitionRow } from "./device-definition-projection";
import { projectDiagnosticDefinitionRow } from "./diagnostic-definition-projection";
import { effectiveParameters } from "./shared/effective-parameters";
import { projectTreatmentDefinitionRow } from "./treatment-definition-projection";

export function orderSetDraftProcedure(url: string, version: number) {
  const rows = getDataStoreClient().getCollection<ActivityDefinitionRow, string>("activityDefinition").toArray;
  const row = rows.find((row) => row.url === url && Number(row.version) === version);
  if (!row) throw new Error(`Procedure '${url}|${version}' is not loaded`);
  const diagnostic = row.catalogModelKind === DIAGNOSTIC_DEFINITION_KIND;
  const definition = diagnostic ? projectDiagnosticDefinitionRow(row) : projectTreatmentDefinitionRow(row);
  const requirements =
    "requirements" in definition
      ? definition.requirements.devices
      : definition.deviceRequirements.map((item) => ({ id: item.id, definitionUrl: item.definition }));
  const devices = getDataStoreClient()
    .getCollection<DeviceDefinitionRow, string>("deviceDefinition")
    .toArray.map(projectDeviceDefinitionRow)
    .sort((left, right) => left.version - right.version);
  const currentDevices = new Map(devices.map((device) => [device.definitionUrl, device]));
  for (const requirement of requirements) {
    const device = currentDevices.get(requirement.definitionUrl);
    if (!device) throw new Error(`Device definition '${requirement.definitionUrl}' is not loaded`);
    materializeDefinition({
      url: device.definitionUrl,
      version: String(device.version),
      availability: device.availability,
      isCurrent: true,
    });
  }
  const parameters = effectiveParameters(definition.effectiveParameters, requirements, currentDevices);
  return {
    name: definition.name,
    kind: diagnostic ? ("diagnostic" as const) : ("treatment" as const),
    pin: {
      url,
      version: String(version),
      availability: definition.availability,
      isCurrent: !rows.some((row) => row.url === url && Number(row.version) > version),
    },
    parameters,
    declarations: parameters.map((parameter) => ({
      ...parameter,
      source: parameter.source === "self" ? ("self" as const) : asMemberId(parameter.source),
      parameterId: asMemberId(parameter.parameterId),
      value:
        parameter.value.kind === "Choice"
          ? {
              ...parameter.value,
              options: parameter.value.options.map((option) => ({ ...option, id: asMemberId(option.id) })),
            }
          : parameter.value,
    })),
  };
}

function draftAnswer(answer: AddOrderInput["parameterValues"][number]): ParameterAnswer {
  return {
    source: answer.source === "self" ? "self" : asMemberId(answer.source),
    parameterId: asMemberId(answer.parameterId),
    value:
      answer.value.kind === "Choice"
        ? { ...answer.value, selected: answer.value.selected.map(asMemberId) }
        : answer.value,
  };
}

function resolveChange(
  set: OrderSet,
  change: OrderSetChange,
  scheduling: OrderSetSchedulingFacts,
): OrderSetCurationChange {
  switch (change.kind) {
    case "addOrder": {
      const facts = orderSetDraftProcedure(change.definitionUrl, change.expectedVersion);
      return {
        kind: "addOrder",
        args: [
          {
            orderId: change.orderId,
            definition: facts.pin,
            parameterValues: change.parameterValues.map(draftAnswer),
          },
          facts.declarations,
          { ...scheduling, concurrentWithStep: change.concurrentWithStep },
        ],
      };
    }
    case "revokeOrder":
      return { kind: "revokeOrder", args: [change.orderId, new Date().toISOString()] };
    case "splitOrder":
      return { kind: "splitOrder", args: [change.orderId, scheduling] };
    case "moveOrder":
      return { kind: "moveOrder", args: [change.orderId, change.toStep, scheduling] };
    case "resizeStep":
      return { kind: "resizeStep", args: [change.stepIndex, change.durationMinutes, scheduling] };
    case "reorderStep":
      return { kind: "reorderStep", args: [change.stepIndex, change.toStep, scheduling] };
    case "moveStep":
      return { kind: "moveStep", args: [change.stepIndex, change.offsetMinutes, scheduling] };
    case "setEnvelope":
      return { kind: "setEnvelopeDuration", args: [change.envelopeMinutes, scheduling] };
    case "resetTiming": {
      const pin = set.protocol;
      if (!pin) throw new Error("This order set has no protocol timing to reset to");
      const row = getDataStoreClient()
        .getCollection<PlanDefinitionRow, string>("planDefinition")
        .toArray.find((row) => row.url === pin.url && row.version === pin.version);
      if (!row) throw new Error(`Protocol '${pin.url}|${pin.version}' is not loaded`);
      const protocol = readProtocolDefinition(row);
      const placements = protocol.steps.flatMap((step) =>
        step.occurrences.flatMap((occurrence) =>
          set.orders
            .filter((order) => order.sourceOccurrenceId === occurrence.id)
            .map((order) => ({ orderId: order.orderId, placement: step.placement })),
        ),
      );
      return { kind: "resetTiming", args: [{ placements, envelopeMinutes: protocol.envelopeMinutes }, scheduling] };
    }
    case "replacePlannedTrials": {
      const order = set.orders.find((candidate) => candidate.orderId === change.orderId);
      if (!order) throw new Error(`Order '${change.orderId}' is not in this set`);
      const { declarations } = orderSetDraftProcedure(order.definition.url, Number(order.definition.version));
      return {
        kind: "replacePlannedTrials",
        args: [
          change.orderId,
          change.plannedTrials.map((trial) => ({
            id: asPlannedTrialId(trial.id),
            values: trial.values.map(draftAnswer),
          })),
          declarations,
        ],
      };
    }
  }
}

export function replayOrderSetChanges(
  props: OrderSetProps,
  changes: readonly OrderSetChange[],
  scheduling: OrderSetSchedulingFacts = { booked: false },
): OrderSet {
  const set = OrderSet.restore(props);
  for (const change of changes) set.applyChange(resolveChange(set, change, scheduling), scheduling);
  return set;
}
